import type { CurrenciesData, Currency, Price, Range } from "@broadside/common";
import { NullContextError, type ChildrenProp } from "@charterindex/armoury-www";
import { convertCurrency } from "@broadside/common";
import { createContext, useContext, useMemo } from "react";


export type CurrencyConversionContext = Readonly<{
	convertPrice: (price: Price, to: Currency) => Price;
	convertPriceFilter: (convert: { filter: Range<number> | undefined, from: Currency, to: Currency }) => Range<number> | undefined;
}>;

const currencyConversionContext = createContext<CurrencyConversionContext | null>(null);

export const CurrencyConversionProvider = (p: ChildrenProp & { data: CurrenciesData }): JSX.Element => {
	const value = useMemo<CurrencyConversionContext>(() => {
		return {
			convertPrice: (price, to) => {
				const amount = convertCurrency(p.data, {
					from: price.currency,
					to,
					amount: price.amount,
				});

				return { currency: to, amount };
			},
			convertPriceFilter: convert => {
				if(!convert.filter) {
					return undefined;
				}

				if(Array.isArray(convert.filter)) {
					return convert.filter.map(amount => convertCurrency(p.data, {
						from: convert.from,
						to: convert.to,
						amount,
					})) as Range<number>;
				}

				return convertCurrency(p.data, {
					from: convert.from,
					to: convert.to,
					amount: convert.filter,
				});
			},
		};
	}, [ p.data ]);

	return (
		<currencyConversionContext.Provider value={ value }>
			{ p.children }
		</currencyConversionContext.Provider>
	);
};

export const useCurrencyConversion = (): CurrencyConversionContext => {
	const ctx = useContext(currencyConversionContext);
	if(!ctx) {
		throw new NullContextError("currencyConversionContext", "CurrencyConversionProvider");
	}

	return ctx;
};
